import { AppError } from "../../../shared/utils/error-handler";
import type { CompanyPricingRepository } from "../../repositories/company/company-pricing-repository";
import type {
  WasteRateSetting,
  WasteRateSettingRequest,
  ShippingFeeSetting,
  ShippingFeeSettingRequest,
  EventDiscount,
  EventDiscountRequest,
} from "../../types/pricing";

export class CompanyPricingUsecase {
  constructor(private companyPricingRepository: CompanyPricingRepository) {}

  // ========== 손지율 비용 관리 ==========

  async getWasteRates(companyId: number): Promise<WasteRateSetting[]> {
    try {
      return await this.companyPricingRepository.getWasteRates(companyId);
    } catch (error) {
      throw new AppError(
        String(error) || "손지율 설정을 불러오지 못했습니다.",
        "WASTE_RATES_GET_FAILED",
        error,
      );
    }
  }

  async createWasteRate(
    companyId: number,
    request: WasteRateSettingRequest,
  ): Promise<WasteRateSetting> {
    try {
      return await this.companyPricingRepository.createWasteRate(
        companyId,
        request,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "손지율 설정을 생성하지 못했습니다.",
        "WASTE_RATE_CREATE_FAILED",
        error,
      );
    }
  }

  async updateWasteRate(
    companyId: number,
    wasteRateId: number,
    request: WasteRateSettingRequest,
  ): Promise<WasteRateSetting> {
    try {
      return await this.companyPricingRepository.updateWasteRate(
        companyId,
        wasteRateId,
        request,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "손지율 설정을 수정하지 못했습니다.",
        "WASTE_RATE_UPDATE_FAILED",
        error,
      );
    }
  }

  async deleteWasteRate(
    companyId: number,
    wasteRateId: number,
  ): Promise<boolean> {
    try {
      return await this.companyPricingRepository.deleteWasteRate(
        companyId,
        wasteRateId,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "손지율 설정을 삭제하지 못했습니다.",
        "WASTE_RATE_DELETE_FAILED",
        error,
      );
    }
  }

  // ========== 배송비 관리 ==========

  async getShippingFee(companyId: number): Promise<ShippingFeeSetting | null> {
    try {
      return await this.companyPricingRepository.getShippingFee(companyId);
    } catch (error) {
      throw new AppError(
        String(error) || "배송비 설정을 불러오지 못했습니다.",
        "SHIPPING_FEE_GET_FAILED",
        error,
      );
    }
  }

  async updateShippingFee(
    companyId: number,
    request: ShippingFeeSettingRequest,
  ): Promise<ShippingFeeSetting> {
    try {
      return await this.companyPricingRepository.updateShippingFee(
        companyId,
        request,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "배송비 설정을 저장하지 못했습니다.",
        "SHIPPING_FEE_UPDATE_FAILED",
        error,
      );
    }
  }

  // ========== 이벤트 할인 관리 ==========

  async getEventDiscounts(companyId: number): Promise<EventDiscount[]> {
    try {
      return await this.companyPricingRepository.getEventDiscounts(companyId);
    } catch (error) {
      throw new AppError(
        String(error) || "이벤트 할인 목록을 불러오지 못했습니다.",
        "EVENT_DISCOUNTS_GET_FAILED",
        error,
      );
    }
  }

  async createEventDiscount(
    companyId: number,
    request: EventDiscountRequest,
  ): Promise<EventDiscount> {
    try {
      return await this.companyPricingRepository.createEventDiscount(
        companyId,
        request,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "이벤트 할인을 생성하지 못했습니다.",
        "EVENT_DISCOUNT_CREATE_FAILED",
        error,
      );
    }
  }

  async updateEventDiscount(
    companyId: number,
    eventDiscountId: number,
    request: EventDiscountRequest,
  ): Promise<EventDiscount> {
    try {
      return await this.companyPricingRepository.updateEventDiscount(
        companyId,
        eventDiscountId,
        request,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "이벤트 할인을 수정하지 못했습니다.",
        "EVENT_DISCOUNT_UPDATE_FAILED",
        error,
      );
    }
  }

  async deleteEventDiscount(
    companyId: number,
    eventDiscountId: number,
  ): Promise<boolean> {
    try {
      return await this.companyPricingRepository.deleteEventDiscount(
        companyId,
        eventDiscountId,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "이벤트 할인을 삭제하지 못했습니다.",
        "EVENT_DISCOUNT_DELETE_FAILED",
        error,
      );
    }
  }
}
